/* eslint-disable camelcase */
import React from "react"
import { useFormik } from "formik"
import * as yup from "yup"

import ErrorChip from "@components/ui/ErrorChip/ErrorChip"
import { TextInput, TextInputProps } from "@components/ui/TextInput/TextInput"
import { Option } from "@framework/types/utils"
import Button from "@components/ui/Button/Button"
import Loader from "@components/ui/Loader/BarLoader"
import List from "@components/ui/List/List"

import styles from "./AddDataConnector.module.sass"

export interface FormDataType {
  name: string
  client_id: string
  client_secret: string
}

export const formFields: Option<keyof FormDataType, TextInputProps>[] = [
  { name: "name", value: { placeholder: "Connector name" } },
  { name: "client_id", value: { placeholder: "Client ID" } },
  {
    name: "client_secret",
    value: { placeholder: "Client Secret" },
  },
]

export const validationSchema = yup.object().shape({
  name: yup.string().required("Field is required"),
  client_id: yup.string().required("Field is required"),
  client_secret: yup.string().required("Field is required"),
})

const defaultInitialValues: FormDataType = {
  name: "",
  client_id: "",
  client_secret: "",
}

export interface ConnectorOAuthFormProps {
  initialValues?: Partial<FormDataType>
  isLoading?: boolean
  onSubmit: (form: FormDataType) => void
}

const ConnectorOAuthForm: React.FC<ConnectorOAuthFormProps> = ({
  isLoading,
  initialValues = null,
  onSubmit,
}) => {
  const formik = useFormik<FormDataType>({
    initialValues: { ...defaultInitialValues, ...initialValues },
    validationSchema,
    onSubmit,
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <List gutter="24">
        {formFields.map((item) => {
          const value = formik.values[item.name]
          const error = formik.errors[item.name]
          const isTouched = formik.touched[item.name]
          const hasError = !!error && isTouched
          return (
            <TextInput
              {...item.value}
              id={item.name}
              name={item.name}
              defaultValue={value}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              withError={!!error && isTouched}
              disabled={isLoading}
              after={
                hasError && (
                  <div className={styles.after}>
                    <ErrorChip messagePlacement="left" message={error} />
                  </div>
                )
              }
              key={item.name}
            />
          )
        })}

        <Button
          disabled={isLoading}
          color="primary"
          type="submit"
          after={isLoading && <Loader />}
        >
          Connect
        </Button>
      </List>
    </form>
  )
}

export default ConnectorOAuthForm
